import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Activity,
  AlertCircle,
  Bot,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Clock,
  Copy,
  Database,
  ExternalLink,
  Eye,
  Filter,
  FileText,
  Fingerprint,
  Globe,
  Languages,
  Laptop,
  Link2,
  MapPin,
  Monitor,
  Network,
  RefreshCw,
  Search,
  Server,
  ShieldCheck,
  Smartphone,
  Tablet,
  Trash2,
  Users,
  X,
} from "lucide-react";

// ============================================================
// CONFIG
// ============================================================

const API_URL = (
  process.env.REACT_APP_API_URL || "https://pmswebbackend.pixelmindsolutions.com"
).replace(/\/+$/, "");

const SAMPLE_SIZE = 100; // backend maximum per request
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const AUTO_REFRESH_MS = 30000;

const DEVICE_META = {
  desktop: { label: "Desktop", icon: Laptop, bar: "bg-orange-500" },
  mobile: { label: "Mobile", icon: Smartphone, bar: "bg-sky-500" },
  tablet: { label: "Tablet", icon: Tablet, bar: "bg-violet-500" },
  bot: { label: "Bot", icon: Bot, bar: "bg-gray-500" },
  unknown: { label: "Unknown", icon: Monitor, bar: "bg-gray-300" },
};

const LOOKUP_META = {
  success: {
    label: "Located",
    style: "bg-green-50 text-green-700 ring-green-200",
    help: "",
  },
  private: {
    label: "Private IP",
    style: "bg-amber-50 text-amber-700 ring-amber-200",
    help: 'The server received a private or local IP address, so it can\'t be located. On a live site this usually means Express needs app.set("trust proxy", 1), or the request was sent from your own server instead of the visitor\'s browser.',
  },
  failed: {
    label: "Lookup failed",
    style: "bg-red-50 text-red-700 ring-red-200",
    help: "The IP address was public, but the location service didn't return a result. This is usually a rate limit or a timeout. Check the error below.",
  },
  invalid: {
    label: "Invalid IP",
    style: "bg-gray-100 text-gray-600 ring-gray-200",
    help: "No valid IP address was found on the request.",
  },
};

// ============================================================
// HELPERS
// ============================================================

const hasValue = (value) =>
  !(
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );

const displayValue = (value) => {
  if (!hasValue(value)) return "—";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const timeAgo = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const seconds = Math.round((Date.now() - date.getTime()) / 1000);

  if (seconds < 45) return "Just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} hr ago`;
  if (seconds < 604800) {
    const days = Math.round(seconds / 86400);
    return `${days} ${days === 1 ? "day" : "days"} ago`;
  }

  return date.toLocaleDateString();
};

// ipapi.co returns offsets such as "+0530"
const formatUtcOffset = (value) => {
  if (!hasValue(value)) return "—";
  const text = Array.isArray(value) ? value[0] : String(value);
  const match = text.match(/^([+-])(\d{2}):?(\d{2})$/);
  return match ? `UTC${match[1]}${match[2]}:${match[3]}` : text;
};

const flagEmoji = (code) =>
  typeof code === "string" && /^[A-Za-z]{2}$/.test(code)
    ? String.fromCodePoint(
        ...code
          .toUpperCase()
          .split("")
          .map((char) => 127397 + char.charCodeAt(0))
      )
    : "🌐";

const getLocation = (track) =>
  [track.city, track.region, track.country].filter(Boolean).join(", ") ||
  "Unknown location";

const getBrowserLabel = (track) =>
  [track.browser, (track.browserVersion || "").split(".")[0]]
    .filter(Boolean)
    .join(" ") || "Unknown browser";

const getOsLabel = (track) =>
  [track.operatingSystem, track.osVersion].filter(Boolean).join(" ") ||
  "Unknown OS";

const getReferrerLabel = (referrer) => {
  if (!referrer) return "Direct visit";
  try {
    return new URL(referrer).hostname || referrer;
  } catch {
    return referrer;
  }
};

const getFriendlyError = (error, fallback) => {
  if (error instanceof TypeError) {
    return `Can't reach the tracking server at ${API_URL}. Check that the backend is running and that CORS allows this site.`;
  }
  return error?.message || fallback;
};

async function request(path, options = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { Accept: "application/json", ...(options.headers || {}) },
  });

  let result = null;

  try {
    result = await response.json();
  } catch {
    // non-JSON response
  }

  if (!response.ok || !result?.success) {
    throw new Error(result?.message || `Request failed (${response.status}).`);
  }

  return result;
}

function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

// ============================================================
// SMALL COMPONENTS
// ============================================================

const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse rounded-lg bg-gray-100 ${className}`} />
);

const StatusBadge = ({ status }) => {
  const meta = LOOKUP_META[status] || LOOKUP_META.invalid;

  return (
    <span
      title={meta.help || undefined}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${meta.style}`}
    >
      {status === "success" ? (
        <CheckCircle2 size={13} />
      ) : (
        <CircleAlert size={13} />
      )}
      {meta.label}
    </span>
  );
};

function CopyButton({ text, label = "Copy" }) {
  const [copied, setCopied] = useState(false);

  const copy = async (event) => {
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable (e.g. insecure context)
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? "Copied" : label}
      aria-label={label}
      className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
    >
      {copied ? (
        <Check size={14} className="text-green-600" />
      ) : (
        <Copy size={14} />
      )}
    </button>
  );
}

const StatCard = ({ label, value, hint, icon: Icon, loading }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-500">{label}</p>

        {loading ? (
          <Skeleton className="mt-3 h-9 w-24" />
        ) : (
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
        )}

        <p className="mt-1.5 text-xs text-gray-400">{hint}</p>
      </div>

      <div className="rounded-xl bg-orange-50 p-3 text-orange-600">
        <Icon size={20} />
      </div>
    </div>
  </div>
);

const Panel = ({ title, subtitle, children }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
    <h3 className="font-semibold text-gray-900">{title}</h3>
    {subtitle && <p className="mt-0.5 text-xs text-gray-400">{subtitle}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

const BarRow = ({ label, count, percent, prefix, barClass = "bg-orange-400" }) => (
  <div>
    <div className="mb-1 flex items-center justify-between gap-3 text-sm">
      <span className="flex min-w-0 items-center gap-2 text-gray-700">
        {prefix}
        <span className="truncate">{label}</span>
      </span>
      <span className="shrink-0 text-gray-500">
        {count} <span className="text-gray-400">({percent}%)</span>
      </span>
    </div>

    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
      <div
        className={`h-full rounded-full ${barClass}`}
        style={{ width: `${Math.max(percent, 3)}%` }}
      />
    </div>
  </div>
);

const EmptyPanel = ({ children }) => (
  <p className="py-6 text-center text-sm text-gray-400">{children}</p>
);

const DetailItem = ({ label, value, icon: Icon, wide = false, children }) => (
  <div
    className={`min-w-0 rounded-xl border border-gray-100 bg-gray-50 p-3 ${
      wide ? "sm:col-span-2 lg:col-span-3" : ""
    }`}
  >
    <div className="mb-1.5 flex items-center gap-2 text-xs font-medium text-gray-500">
      {Icon && <Icon size={14} className="shrink-0 text-orange-500" />}
      {label}
    </div>

    <div
      className={`break-words text-sm font-semibold leading-6 ${
        hasValue(value) ? "text-gray-800" : "text-gray-300"
      }`}
    >
      {children || displayValue(value)}
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <section>
    <h3 className="mb-3 text-sm font-bold text-gray-900">{title}</h3>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </div>
  </section>
);

function ConfirmDialog({
  title,
  message,
  confirmLabel,
  requireText,
  loading,
  onConfirm,
  onCancel,
}) {
  const [typed, setTyped] = useState("");
  const canConfirm = !requireText || typed === requireText;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-950/50 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !loading) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-red-50 p-2.5 text-red-600">
            <Trash2 size={20} />
          </div>

          <div>
            <h2 id="confirm-title" className="font-bold text-gray-900">
              {title}
            </h2>
            <p className="mt-1 text-sm text-gray-500">{message}</p>
          </div>
        </div>

        {requireText && (
          <div className="mt-4">
            <label className="text-sm text-gray-600">
              Type <span className="font-semibold">{requireText}</span> to
              confirm
              <input
                autoFocus
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-red-400 focus:ring-4 focus:ring-red-100"
              />
            </label>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm || loading}
            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading && <RefreshCw size={15} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function VisitorIdentity({ track }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <div
        aria-hidden="true"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-50 text-lg"
      >
        {flagEmoji(track.countryCode)}
      </div>

      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <span className="truncate font-semibold text-gray-900">
            {displayValue(track.ipAddress)}
          </span>
          {track.ipAddress && (
            <CopyButton text={track.ipAddress} label="Copy IP address" />
          )}
        </div>

        <p className="text-xs text-gray-400">
          {track.ipVersion ? `IPv${track.ipVersion}` : "IP version unknown"}
        </p>

        {track.ipLookupStatus && track.ipLookupStatus !== "success" && (
          <div className="mt-1">
            <StatusBadge status={track.ipLookupStatus} />
          </div>
        )}
      </div>
    </div>
  );
}

function RowActions({ track, deleting, onView, onDelete }) {
  return (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={() => onView(track)}
        title="View details"
        aria-label="View visitor details"
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition hover:border-orange-200 hover:bg-orange-50 hover:text-orange-600"
      >
        <Eye size={16} />
        Details
      </button>

      <button
        type="button"
        onClick={() => onDelete(track)}
        disabled={deleting}
        title="Delete record"
        aria-label="Delete visitor record"
        className="rounded-lg border border-gray-200 p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
      >
        {deleting ? (
          <RefreshCw size={16} className="animate-spin" />
        ) : (
          <Trash2 size={16} />
        )}
      </button>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function Tracker() {
  // table data
  const [tracks, setTracks] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);

  // overview data (latest visits, unfiltered)
  const [overview, setOverview] = useState({
    total: 0,
    sample: [],
    loading: true,
  });

  // filters
  const [search, setSearch] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("");
  const [countryInput, setCountryInput] = useState("");
  const [routeInput, setRouteInput] = useState("");
  const [ipInput, setIpInput] = useState("");
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const country = useDebouncedValue(countryInput);
  const pageRoute = useDebouncedValue(routeInput);
  const ipAddress = useDebouncedValue(ipInput);

  // UI state
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type: "one", track} | {type: "all"}
  const [deletingId, setDeletingId] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const requestId = useRef(0);

  // ----------------------------------------------------------
  // DATA LOADING
  // ----------------------------------------------------------

  const fetchTracks = useCallback(
    async ({ silent = false } = {}) => {
      const id = ++requestId.current;

      if (!silent) setLoading(true);

      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(pageSize),
        });

        if (country.trim()) params.set("country", country.trim());
        if (deviceFilter) params.set("deviceType", deviceFilter);
        if (pageRoute.trim()) params.set("pageRoute", pageRoute.trim());
        if (ipAddress.trim()) params.set("ipAddress", ipAddress.trim());

        const result = await request(`/api/track?${params.toString()}`);

        if (id !== requestId.current) return;

        const records = Array.isArray(result.data) ? result.data : [];

        setTracks(records);
        setPagination(
          result.pagination || {
            total: records.length,
            page,
            limit: pageSize,
            totalPages: records.length ? 1 : 0,
          }
        );
        setError("");
        setLastUpdated(new Date());
      } catch (err) {
        if (id === requestId.current) {
          setError(getFriendlyError(err, "Unable to load visitor records."));
        }
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    [page, pageSize, country, deviceFilter, pageRoute, ipAddress]
  );

  const fetchOverview = useCallback(async () => {
    try {
      const result = await request(`/api/track?page=1&limit=${SAMPLE_SIZE}`);
      const sample = Array.isArray(result.data) ? result.data : [];

      setOverview({
        total: result.pagination?.total ?? sample.length,
        sample,
        loading: false,
      });
    } catch {
      setOverview((current) => ({ ...current, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchTracks();
  }, [fetchTracks]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // go back to page 1 whenever a server-side filter changes
  useEffect(() => {
    setPage(1);
  }, [country, pageRoute, ipAddress, deviceFilter, pageSize]);

  // optional auto refresh
  useEffect(() => {
    if (!autoRefresh) return undefined;

    const timer = setInterval(() => {
      if (document.hidden) return;
      fetchTracks({ silent: true });
      fetchOverview();
    }, AUTO_REFRESH_MS);

    return () => clearInterval(timer);
  }, [autoRefresh, fetchTracks, fetchOverview]);

  // auto-dismiss success message
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  // close dialogs with Escape
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (confirm) {
        if (!deletingAll && !deletingId) setConfirm(null);
      } else {
        setSelected(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [confirm, deletingAll, deletingId]);

  const refreshAll = () => {
    setNotice("");
    fetchTracks();
    fetchOverview();
  };

  // ----------------------------------------------------------
  // OVERVIEW STATS (latest 100 visits)
  // ----------------------------------------------------------

  const insights = useMemo(() => {
    const sample = overview.sample;
    const count = sample.length;
    const pct = (value) => (count ? Math.round((value / count) * 100) : 0);

    const countryMap = new Map();
    const pageMap = new Map();
    const deviceMap = new Map();
    const lookup = { success: 0, private: 0, failed: 0, invalid: 0 };
    const uniqueIps = new Set();

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    let today = 0;

    sample.forEach((track) => {
      if (track.country) {
        const entry = countryMap.get(track.country) || {
          name: track.country,
          code: track.countryCode,
          count: 0,
        };
        entry.count += 1;
        countryMap.set(track.country, entry);
      }

      const route = track.path || track.page;
      if (route) pageMap.set(route, (pageMap.get(route) || 0) + 1);

      const device = DEVICE_META[track.deviceType] ? track.deviceType : "unknown";
      deviceMap.set(device, (deviceMap.get(device) || 0) + 1);

      const status = lookup[track.ipLookupStatus] !== undefined
        ? track.ipLookupStatus
        : "invalid";
      lookup[status] += 1;

      if (track.ipAddress) uniqueIps.add(track.ipAddress);

      const visited = new Date(track.visitDate || track.createdAt);
      if (visited >= startOfToday) today += 1;
    });

    const oldest = sample.length
      ? new Date(
          sample[sample.length - 1].visitDate ||
            sample[sample.length - 1].createdAt
        )
      : null;

    // if all loaded visits happened today, the true number may be higher
    const todayIsCapped =
      count >= SAMPLE_SIZE && oldest && oldest >= startOfToday;

    const toRows = (map, limit) =>
      [...map.entries()]
        .map(([name, value]) => ({ name, count: value }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit)
        .map((item) => ({ ...item, percent: pct(item.count) }));

    const devices = Object.keys(DEVICE_META)
      .map((key) => ({
        key,
        count: deviceMap.get(key) || 0,
        percent: pct(deviceMap.get(key) || 0),
      }))
      .filter((item) => item.count > 0);

    return {
      count,
      today: todayIsCapped ? `${today}+` : today,
      uniqueVisitors: uniqueIps.size,
      countryCount: countryMap.size,
      countries: [...countryMap.values()]
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)
        .map((item) => ({ ...item, percent: pct(item.count) })),
      pages: toRows(pageMap, 5),
      devices,
      lookup,
      locatedPercent: pct(lookup.success),
    };
  }, [overview.sample]);

  // ----------------------------------------------------------
  // LOCAL SEARCH ON LOADED ROWS
  // ----------------------------------------------------------

  const filteredTracks = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return tracks;

    return tracks.filter((track) =>
      [
        track.page,
        track.path,
        track.queryString,
        track.ipAddress,
        track.country,
        track.countryCode,
        track.region,
        track.city,
        track.postalCode,
        track.isp,
        track.organization,
        track.asn,
        track.browser,
        track.operatingSystem,
        track.deviceType,
        track.userAgent,
        track.referrer,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [tracks, search]);

  // ----------------------------------------------------------
  // ACTIONS
  // ----------------------------------------------------------

  const openDetails = (track) => {
    setSelected(track);

    // refresh from the server in the background
    request(`/api/track/${encodeURIComponent(track._id)}`)
      .then((result) =>
        setSelected((current) =>
          current?._id === track._id ? result.data : current
        )
      )
      .catch(() => {
        // keep the data already shown
      });
  };

  const deleteOne = async (track) => {
    setDeletingId(track._id);

    try {
      await request(`/api/track/${encodeURIComponent(track._id)}`, {
        method: "DELETE",
      });

      setSelected((current) => (current?._id === track._id ? null : current));
      setConfirm(null);
      setNotice("Visitor record deleted.");

      if (tracks.length === 1 && page > 1) {
        setPage((current) => current - 1);
      } else {
        fetchTracks();
      }

      fetchOverview();
    } catch (err) {
      setConfirm(null);
      setError(getFriendlyError(err, "Unable to delete the visitor record."));
    } finally {
      setDeletingId(null);
    }
  };

  const deleteAll = async () => {
    setDeletingAll(true);

    try {
      const result = await request("/api/track?confirm=true", {
        method: "DELETE",
      });

      setConfirm(null);
      setSelected(null);
      setNotice(
        `Deleted ${Number(result.deletedCount || 0).toLocaleString()} visitor records.`
      );

      setPage(1);
      fetchTracks();
      fetchOverview();
    } catch (err) {
      setConfirm(null);
      setError(getFriendlyError(err, "Unable to delete visitor records."));
    } finally {
      setDeletingAll(false);
    }
  };

  const activeFilters = [
    country.trim() && {
      key: "country",
      label: `Country: ${country.trim()}`,
      clear: () => setCountryInput(""),
    },
    deviceFilter && {
      key: "device",
      label: `Device: ${DEVICE_META[deviceFilter]?.label || deviceFilter}`,
      clear: () => setDeviceFilter(""),
    },
    pageRoute.trim() && {
      key: "route",
      label: `Page: ${pageRoute.trim()}`,
      clear: () => setRouteInput(""),
    },
    ipAddress.trim() && {
      key: "ip",
      label: `IP: ${ipAddress.trim()}`,
      clear: () => setIpInput(""),
    },
  ].filter(Boolean);

  const clearFilters = () => {
    setSearch("");
    setCountryInput("");
    setDeviceFilter("");
    setRouteInput("");
    setIpInput("");
  };

  const totalPages = Math.max(1, pagination.totalPages || 1);
  const rangeStart = tracks.length ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = rangeStart ? rangeStart + tracks.length - 1 : 0;
  const hasAnyFilter = activeFilters.length > 0 || search.trim();

  const lookupProblems = insights.lookup.private + insights.lookup.failed;

  const selectedDevice = selected
    ? DEVICE_META[selected.deviceType] || DEVICE_META.unknown
    : DEVICE_META.unknown;

  const selectedMeta = selected
    ? LOOKUP_META[selected.ipLookupStatus] || LOOKUP_META.invalid
    : null;

  // ----------------------------------------------------------
  // RENDER
  // ----------------------------------------------------------

  return (
    <div className="min-h-screen bg-gray-50/80 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-[1500px] space-y-6">
        {/* HEADER */}

        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-orange-600">
              <Activity size={17} />
              Website analytics
            </div>

            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              Visitor tracker
            </h1>

            <p className="mt-1 text-sm text-gray-500">
              See who visits your site, where they come from, and what device
              they use.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {lastUpdated && (
              <span className="mr-1 text-xs text-gray-400">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}

            <button
              type="button"
              onClick={() => setAutoRefresh((value) => !value)}
              aria-pressed={autoRefresh}
              title={`Refresh every ${AUTO_REFRESH_MS / 1000} seconds`}
              className={`inline-flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm font-semibold transition ${
                autoRefresh
                  ? "border-green-200 bg-green-50 text-green-700"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              <span className="relative flex h-2.5 w-2.5">
                {autoRefresh && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                )}
                <span
                  className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
                    autoRefresh ? "bg-green-500" : "bg-gray-300"
                  }`}
                />
              </span>
              Auto-refresh
            </button>

            <button
              type="button"
              onClick={refreshAll}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {/* MESSAGES */}

        {error && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
          >
            <AlertCircle size={19} className="mt-0.5 shrink-0" />

            <div className="flex-1">{error}</div>

            <button
              type="button"
              onClick={refreshAll}
              className="font-semibold underline underline-offset-2"
            >
              Try again
            </button>

            <button
              type="button"
              onClick={() => setError("")}
              aria-label="Dismiss error"
            >
              <X size={17} />
            </button>
          </div>
        )}

        {notice && (
          <div
            role="status"
            className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700"
          >
            <CheckCircle2 size={18} />
            {notice}
          </div>
        )}

        {/* HEADLINE STATS */}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Total visits"
            value={overview.total}
            hint="Every visit stored so far"
            icon={Users}
            loading={overview.loading}
          />
          <StatCard
            label="Visits today"
            value={insights.today}
            hint="Since midnight, your local time"
            icon={CalendarDays}
            loading={overview.loading}
          />
          <StatCard
            label="Unique visitors"
            value={insights.uniqueVisitors}
            hint={`Different IP addresses in the latest ${insights.count}`}
            icon={Fingerprint}
            loading={overview.loading}
          />
          <StatCard
            label="Countries"
            value={insights.countryCount}
            hint={`Seen in the latest ${insights.count} visits`}
            icon={Globe}
            loading={overview.loading}
          />
        </div>

        {/* INSIGHTS */}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
          <Panel title="Top countries" subtitle="Where visitors come from">
            {overview.loading ? (
              <Skeleton className="h-32 w-full" />
            ) : insights.countries.length ? (
              <div className="space-y-4">
                {insights.countries.map((item) => (
                  <BarRow
                    key={item.name}
                    label={item.name}
                    count={item.count}
                    percent={item.percent}
                    prefix={<span aria-hidden="true">{flagEmoji(item.code)}</span>}
                  />
                ))}
              </div>
            ) : (
              <EmptyPanel>No locations yet.</EmptyPanel>
            )}
          </Panel>

          <Panel title="Devices" subtitle="What visitors browse on">
            {overview.loading ? (
              <Skeleton className="h-32 w-full" />
            ) : insights.devices.length ? (
              <>
                <div className="flex h-3 overflow-hidden rounded-full bg-gray-100">
                  {insights.devices.map((item) => (
                    <div
                      key={item.key}
                      className={DEVICE_META[item.key].bar}
                      style={{ width: `${item.percent}%` }}
                      title={`${DEVICE_META[item.key].label}: ${item.percent}%`}
                    />
                  ))}
                </div>

                <ul className="mt-4 space-y-2.5">
                  {insights.devices.map((item) => {
                    const Icon = DEVICE_META[item.key].icon;

                    return (
                      <li
                        key={item.key}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="flex items-center gap-2 text-gray-700">
                          <span
                            className={`h-2.5 w-2.5 rounded-full ${DEVICE_META[item.key].bar}`}
                          />
                          <Icon size={15} className="text-gray-400" />
                          {DEVICE_META[item.key].label}
                        </span>
                        <span className="text-gray-500">
                          {item.count}{" "}
                          <span className="text-gray-400">({item.percent}%)</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : (
              <EmptyPanel>No devices yet.</EmptyPanel>
            )}
          </Panel>

          <Panel title="Popular pages" subtitle="Most visited routes">
            {overview.loading ? (
              <Skeleton className="h-32 w-full" />
            ) : insights.pages.length ? (
              <div className="space-y-4">
                {insights.pages.map((item) => (
                  <BarRow
                    key={item.name}
                    label={item.name}
                    count={item.count}
                    percent={item.percent}
                    barClass="bg-sky-400"
                  />
                ))}
              </div>
            ) : (
              <EmptyPanel>No pages yet.</EmptyPanel>
            )}
          </Panel>

          <Panel
            title="Location accuracy"
            subtitle="How many visits could be located"
          >
            {overview.loading ? (
              <Skeleton className="h-32 w-full" />
            ) : insights.count ? (
              <>
                <p className="text-3xl font-bold text-gray-900">
                  {insights.locatedPercent}%
                </p>

                <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-green-500"
                    style={{ width: `${insights.locatedPercent}%` }}
                  />
                </div>

                <ul className="mt-4 space-y-1.5 text-sm text-gray-600">
                  <li className="flex justify-between">
                    Located <span>{insights.lookup.success}</span>
                  </li>
                  <li className="flex justify-between">
                    Private IP <span>{insights.lookup.private}</span>
                  </li>
                  <li className="flex justify-between">
                    Lookup failed <span>{insights.lookup.failed}</span>
                  </li>
                </ul>

                {lookupProblems > 0 && (
                  <p className="mt-4 rounded-lg bg-amber-50 p-3 text-xs leading-5 text-amber-800">
                    {lookupProblems} of the latest {insights.count} visits
                    have no location. Open a record to see the reason.
                  </p>
                )}
              </>
            ) : (
              <EmptyPanel>No data yet.</EmptyPanel>
            )}
          </Panel>
        </div>

        {/* RECORDS */}

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="space-y-4 border-b border-gray-100 p-4 sm:p-5">
            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  Visitor records
                </h2>

                <p className="mt-0.5 text-sm text-gray-500">
                  {Number(pagination.total || 0).toLocaleString()}{" "}
                  {pagination.total === 1 ? "record" : "records"}
                  {activeFilters.length ? " match your filters" : " in total"}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setConfirm({ type: "all" })}
                disabled={!overview.total}
                className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-red-200 px-3.5 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Trash2 size={16} />
                Delete all records
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[2fr_1fr_1fr_auto]">
              <div className="relative">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search this page by IP, city, page, browser…"
                  aria-label="Search loaded records"
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100"
                />
              </div>

              <input
                type="text"
                value={countryInput}
                onChange={(event) => setCountryInput(event.target.value)}
                placeholder="Country, e.g. India"
                aria-label="Filter by country"
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100"
              />

              <select
                value={deviceFilter}
                onChange={(event) => setDeviceFilter(event.target.value)}
                aria-label="Filter by device"
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100"
              >
                <option value="">All devices</option>
                {Object.entries(DEVICE_META).map(([key, meta]) => (
                  <option key={key} value={key}>
                    {meta.label}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={() => setShowMoreFilters((value) => !value)}
                aria-expanded={showMoreFilters}
                className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${
                  showMoreFilters
                    ? "border-orange-200 bg-orange-50 text-orange-600"
                    : "border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Filter size={16} />
                More filters
              </button>
            </div>

            {showMoreFilters && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="text-xs font-medium text-gray-500">
                  Page route (exact match)
                  <input
                    type="text"
                    value={routeInput}
                    onChange={(event) => setRouteInput(event.target.value)}
                    placeholder="/pricing"
                    className="mt-1.5 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-normal text-gray-900 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100"
                  />
                </label>

                <label className="text-xs font-medium text-gray-500">
                  IP address (exact match)
                  <input
                    type="text"
                    value={ipInput}
                    onChange={(event) => setIpInput(event.target.value)}
                    placeholder="203.0.113.10"
                    className="mt-1.5 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-normal text-gray-900 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100"
                  />
                </label>
              </div>
            )}

            {hasAnyFilter && (
              <div className="flex flex-wrap items-center gap-2">
                {activeFilters.map((filter) => (
                  <span
                    key={filter.key}
                    className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 py-1 pl-3 pr-1.5 text-xs font-medium text-orange-700"
                  >
                    {filter.label}
                    <button
                      type="button"
                      onClick={filter.clear}
                      aria-label={`Remove ${filter.label}`}
                      className="rounded-full p-0.5 hover:bg-orange-100"
                    >
                      <X size={13} />
                    </button>
                  </span>
                ))}

                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-xs font-semibold text-gray-500 underline underline-offset-2 hover:text-orange-600"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* TABLE STATES */}

          {loading && tracks.length === 0 ? (
            <div className="space-y-3 p-5">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredTracks.length === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center p-8 text-center">
              <div className="rounded-2xl bg-orange-50 p-4 text-orange-500">
                <Database size={28} />
              </div>

              <h3 className="mt-4 font-semibold text-gray-900">
                {hasAnyFilter
                  ? "No visitors match your filters"
                  : "No visits recorded yet"}
              </h3>

              <p className="mt-1 max-w-sm text-sm text-gray-500">
                {hasAnyFilter
                  ? "Try a different search, or clear the filters to see everything."
                  : "Visits will show up here as soon as your site sends them to the tracking API."}
              </p>

              {hasAnyFilter && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="mt-4 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className={loading ? "opacity-60 transition" : "transition"}>
              {/* DESKTOP TABLE */}

              <div className="hidden overflow-x-auto lg:block">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 text-xs font-semibold text-gray-500">
                    <tr>
                      <th className="px-5 py-3.5">Visitor</th>
                      <th className="px-5 py-3.5">Page visited</th>
                      <th className="px-5 py-3.5">Location</th>
                      <th className="px-5 py-3.5">Device</th>
                      <th className="px-5 py-3.5">When</th>
                      <th className="px-5 py-3.5 text-right">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {filteredTracks.map((track) => {
                      const device =
                        DEVICE_META[track.deviceType] || DEVICE_META.unknown;
                      const DeviceIcon = device.icon;

                      return (
                        <tr
                          key={track._id}
                          className="transition hover:bg-orange-50/30"
                        >
                          <td className="px-5 py-4">
                            <VisitorIdentity track={track} />
                          </td>

                          <td className="px-5 py-4">
                            <p className="max-w-[260px] break-all font-medium text-gray-800">
                              {displayValue(track.path || track.page)}
                            </p>
                            <p className="mt-1 max-w-[260px] truncate text-xs text-gray-400">
                              From {getReferrerLabel(track.referrer)}
                            </p>
                          </td>

                          <td className="px-5 py-4">
                            <div className="flex items-start gap-2 text-sm text-gray-700">
                              <MapPin
                                size={16}
                                className="mt-0.5 shrink-0 text-orange-500"
                              />
                              <div>
                                <p className="max-w-[240px]">
                                  {getLocation(track)}
                                </p>
                                {track.timezone && (
                                  <p className="mt-1 text-xs text-gray-400">
                                    {track.timezone}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="px-5 py-4">
                            <div className="flex items-start gap-2">
                              <DeviceIcon
                                size={18}
                                className="mt-0.5 shrink-0 text-orange-500"
                              />
                              <div>
                                <p className="font-medium text-gray-800">
                                  {device.label}
                                </p>
                                <p className="mt-0.5 text-xs text-gray-500">
                                  {getBrowserLabel(track)} · {getOsLabel(track)}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="whitespace-nowrap px-5 py-4 text-sm">
                            <p
                              className="font-medium text-gray-700"
                              title={formatDate(track.visitDate || track.createdAt)}
                            >
                              {timeAgo(track.visitDate || track.createdAt)}
                            </p>
                            <p className="mt-0.5 text-xs text-gray-400">
                              {formatDate(track.visitDate || track.createdAt)}
                            </p>
                          </td>

                          <td className="px-5 py-4">
                            <RowActions
                              track={track}
                              deleting={deletingId === track._id}
                              onView={openDetails}
                              onDelete={(item) =>
                                setConfirm({ type: "one", track: item })
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARDS */}

              <ul className="divide-y divide-gray-100 lg:hidden">
                {filteredTracks.map((track) => {
                  const device =
                    DEVICE_META[track.deviceType] || DEVICE_META.unknown;
                  const DeviceIcon = device.icon;

                  return (
                    <li key={track._id} className="space-y-3 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <VisitorIdentity track={track} />
                        <span className="shrink-0 text-xs text-gray-400">
                          {timeAgo(track.visitDate || track.createdAt)}
                        </span>
                      </div>

                      <div className="space-y-1.5 text-sm text-gray-600">
                        <p className="flex items-start gap-2">
                          <FileText size={15} className="mt-0.5 shrink-0 text-orange-500" />
                          <span className="break-all font-medium text-gray-800">
                            {displayValue(track.path || track.page)}
                          </span>
                        </p>

                        <p className="flex items-start gap-2">
                          <MapPin
                            size={15}
                            className="mt-0.5 shrink-0 text-orange-500"
                          />
                          {getLocation(track)}
                        </p>

                        <p className="flex items-start gap-2">
                          <DeviceIcon
                            size={15}
                            className="mt-0.5 shrink-0 text-orange-500"
                          />
                          {device.label} · {getBrowserLabel(track)}
                        </p>
                      </div>

                      <RowActions
                        track={track}
                        deleting={deletingId === track._id}
                        onView={openDetails}
                        onDelete={(item) =>
                          setConfirm({ type: "one", track: item })
                        }
                      />
                    </li>
                  );
                })}
              </ul>

              {/* PAGINATION */}

              <div className="flex flex-col gap-3 border-t border-gray-100 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
                  <p>
                    Showing {rangeStart}–{rangeEnd} of{" "}
                    {Number(pagination.total || 0).toLocaleString()}
                    {search.trim() &&
                      ` · ${filteredTracks.length} match your search on this page`}
                  </p>

                  <label className="flex items-center gap-2">
                    Rows per page
                    <select
                      value={pageSize}
                      onChange={(event) => setPageSize(Number(event.target.value))}
                      className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm"
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <span className="mr-1 text-sm text-gray-500">
                    Page {page} of {totalPages}
                  </span>

                  <button
                    type="button"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>

                  <button
                    type="button"
                    disabled={page >= totalPages || loading}
                    onClick={() =>
                      setPage((current) => Math.min(totalPages, current + 1))
                    }
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* PRIVACY NOTE */}

        <div className="flex items-start gap-2 rounded-xl border border-orange-100 bg-orange-50/60 p-4 text-xs leading-5 text-gray-600">
          <ShieldCheck size={17} className="mt-0.5 shrink-0 text-orange-600" />
          <p>
            Locations come from IP addresses and are approximate. Visitor data
            is personal information, so keep this dashboard limited to
            administrators.
          </p>
        </div>
      </div>

      {/* ======================================================
          VISITOR DETAILS
      ====================================================== */}

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-950/50 p-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelected(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="visitor-details-title"
            className="my-auto max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-5 py-4 sm:px-6">
              <div>
                <h2
                  id="visitor-details-title"
                  className="text-lg font-bold text-gray-900"
                >
                  Visitor details
                </h2>
                <p className="mt-0.5 text-sm text-gray-500">
                  Everything we recorded for this visit
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelected(null)}
                aria-label="Close visitor details"
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-6 p-5 sm:p-6">
              {/* SUMMARY */}

              <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4 sm:p-5">
                <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                  <div className="flex min-w-0 items-start gap-4">
                    <div
                      aria-hidden="true"
                      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-3xl shadow-sm"
                    >
                      {flagEmoji(selected.countryCode)}
                    </div>

                    <div className="min-w-0">
                      <p className="text-xl font-bold text-gray-900">
                        {getLocation(selected)}
                      </p>

                      <p className="mt-1 flex items-center gap-1 text-sm text-gray-600">
                        {displayValue(selected.ipAddress)}
                        {selected.ipAddress && (
                          <CopyButton
                            text={selected.ipAddress}
                            label="Copy IP address"
                          />
                        )}
                      </p>

                      <p className="mt-1 text-sm text-gray-600">
                        Visited{" "}
                        <span className="break-all font-medium text-gray-800">
                          {displayValue(selected.path || selected.page)}
                        </span>{" "}
                        · {timeAgo(selected.visitDate || selected.createdAt)}
                      </p>
                    </div>
                  </div>

                  <StatusBadge status={selected.ipLookupStatus} />
                </div>
              </div>

              {selected.ipLookupStatus !== "success" && selectedMeta?.help && (
                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                  <CircleAlert size={18} className="mt-1 shrink-0" />
                  <div>
                    <p>{selectedMeta.help}</p>
                    {selected.ipLookupError && (
                      <p className="mt-2 break-words rounded-lg bg-white/70 p-2 text-xs">
                        {selected.ipLookupError}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {Number.isFinite(selected.latitude) &&
                Number.isFinite(selected.longitude) && (
                  <a
                    href={`https://www.google.com/maps?q=${encodeURIComponent(
                      `${selected.latitude},${selected.longitude}`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
                  >
                    <MapPin size={17} />
                    Open approximate location in Maps
                    <ExternalLink size={15} />
                  </a>
                )}

              <Section title="Location">
                <DetailItem label="Country" value={selected.country} icon={Globe} />
                <DetailItem label="Country code" value={selected.countryCode} />
                <DetailItem label="Continent" value={selected.continent} />
                <DetailItem
                  label="Region or state"
                  value={selected.region}
                  icon={MapPin}
                />
                <DetailItem label="Region code" value={selected.regionCode} />
                <DetailItem label="City" value={selected.city} />
                <DetailItem label="Postal code" value={selected.postalCode} />
                <DetailItem label="Latitude" value={selected.latitude} />
                <DetailItem label="Longitude" value={selected.longitude} />
                <DetailItem label="Capital" value={selected.capital} />
                <DetailItem
                  label="Calling code"
                  value={selected.callingCode ? `+${selected.callingCode}` : ""}
                />
                <DetailItem label="Currency" value={selected.currency} />
                <DetailItem
                  label="Languages"
                  value={selected.languages}
                  icon={Languages}
                />
              </Section>

              <Section title="Time zone">
                <DetailItem
                  label="Time zone"
                  value={selected.timezone}
                  icon={Clock}
                />
                <DetailItem
                  label="UTC offset"
                  value={selected.timezoneUtc}
                >
                  {formatUtcOffset(selected.timezoneUtc)}
                </DetailItem>
              </Section>

              <Section title="Network">
                <DetailItem
                  label="Internet provider"
                  value={selected.organization || selected.isp}
                  icon={Network}
                />
                <DetailItem label="ASN" value={selected.asn} />
                <DetailItem label="IP version" value={selected.ipVersion} />
                <DetailItem
                  label="Location source"
                  value={selected.ipLookupProvider}
                />
              </Section>

              <Section title="Device and browser">
                <DetailItem
                  label="Device"
                  value={selectedDevice.label}
                  icon={selectedDevice.icon}
                />
                <DetailItem
                  label="Browser"
                  value={getBrowserLabel(selected)}
                  icon={Monitor}
                />
                <DetailItem
                  label="Operating system"
                  value={getOsLabel(selected)}
                  icon={Laptop}
                />
                <DetailItem label="Mobile" value={selected.isMobile} />
                <DetailItem label="Tablet" value={selected.isTablet} />
                <DetailItem label="Bot" value={selected.isBot} />
                <DetailItem
                  label="Browser language"
                  value={selected.browserLanguage}
                  icon={Languages}
                />
                <DetailItem
                  label="User agent"
                  value={selected.userAgent}
                  icon={Fingerprint}
                  wide
                />
              </Section>

              <Section title="Page and request">
                <DetailItem label="Page" value={selected.page} wide />
                <DetailItem label="Path" value={selected.path} />
                <DetailItem label="Query string" value={selected.queryString} />
                <DetailItem
                  label="Came from"
                  value={selected.referrer}
                  icon={Link2}
                  wide
                />
                <DetailItem label="Origin" value={selected.origin} />
                <DetailItem
                  label="Host"
                  value={selected.hostname}
                  icon={Server}
                />
                <DetailItem label="Protocol" value={selected.protocol} />
                <DetailItem label="HTTP method" value={selected.httpMethod} />
              </Section>

              <Section title="Record">
                <DetailItem
                  label="Visited"
                  value={formatDate(selected.visitDate)}
                  icon={CalendarDays}
                />
                <DetailItem
                  label="Saved"
                  value={formatDate(selected.createdAt)}
                />
                <DetailItem
                  label="Record ID"
                  value={selected._id}
                />
              </Section>

              <div className="flex flex-col justify-between gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => setConfirm({ type: "one", track: selected })}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                >
                  <Trash2 size={16} />
                  Delete record
                </button>

                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================
          CONFIRMATIONS
      ====================================================== */}

      {confirm?.type === "one" && (
        <ConfirmDialog
          title="Delete this visitor record?"
          message={`The visit to "${
            confirm.track.path || confirm.track.page || "this page"
          }" from ${confirm.track.ipAddress || "an unknown IP"} will be permanently removed.`}
          confirmLabel="Delete record"
          loading={deletingId === confirm.track._id}
          onCancel={() => setConfirm(null)}
          onConfirm={() => deleteOne(confirm.track)}
        />
      )}

      {confirm?.type === "all" && (
        <ConfirmDialog
          title="Delete all visitor records?"
          message={`This permanently removes all ${Number(
            overview.total || 0
          ).toLocaleString()} records. This can't be undone.`}
          confirmLabel="Delete everything"
          requireText="DELETE"
          loading={deletingAll}
          onCancel={() => setConfirm(null)}
          onConfirm={deleteAll}
        />
      )}
    </div>
  );
}